import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Pagination,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import cleanJobDescription from "./cleanJobDescription";

import { API_URL } from "./config";

export default function CandidateAvailablePositions() {
  const [jobOrders, setJobOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [page, setPage] = useState(1);

  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const jobsPerPage = 7;

  useEffect(() => {
    fetchJobOrders();
  }, []);

  const fetchJobOrders = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/joborder/uprotected`);
      if (!res.ok) throw new Error("Failed to fetch jobs");
      const data = await res.json();
      setJobOrders(data);
    } catch (e) {
      setError(e.message || "Something went wrong fetching jobs.");
      setJobOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobOrders = jobOrders.filter((j) => {
    const q = search.toLowerCase();
    return (
      j.job_title?.toLowerCase().includes(q) ||
      (j.company_name || "").toLowerCase().includes(q) ||
      (j.location || "").toLowerCase().includes(q)
    );
  });

  const pageCount = Math.ceil(filteredJobOrders.length / jobsPerPage) || 1;
  const paginatedJobs = filteredJobOrders.slice(
    (page - 1) * jobsPerPage,
    page * jobsPerPage
  );

  const handleRowClick = (job) => {
    setSelectedJob(job);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedJob(null);
  };

  const handleApply = () => {
    if (selectedJob && selectedJob.id) {
      navigate(`/apply/${selectedJob.id}`);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    // Optional: scroll to top of list on page change
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box sx={{ py: 2, maxWidth: 900, mx: "auto" }}>
      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: 700, letterSpacing: 0.2 }}
      >
        Available Positions
      </Typography>

      <TextField
        placeholder="Search by role, company, or location..."
        variant="outlined"
        size="small"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          width: "100%",
          "& .MuiOutlinedInput-root": {
            borderRadius: 999,
            backgroundColor: "#fff",
            boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
            "& fieldset": {
              borderColor: "rgba(148, 163, 184, 0.4)",
            },
            "&:hover fieldset": {
              borderColor: "rgba(79, 70, 229, 0.7)",
            },
            "&.Mui-focused fieldset": {
              borderColor: theme.palette.primary.main,
              borderWidth: 1.5,
            },
          },
        }}
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      ) : filteredJobOrders.length === 0 ? (
        <Typography sx={{ mt: 2 }}>No available positions found.</Typography>
      ) : (
        <>
          <List disablePadding>
            {paginatedJobs.map((job) => (
              <React.Fragment key={job.id}>
                <ListItemButton
                  sx={{
                    alignItems: "flex-start",
                    py: 2,
                    px: 2,
                    mb: 1.5,
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(246,248,252,0.96))",
                    border: "1px solid rgba(148,163,184,0.2)",
                    boxShadow: "0 6px 18px rgba(15,23,42,0.06)",
                    transition:
                      "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,1), rgba(237,242,255,0.9))",
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 30px rgba(15,23,42,0.14)",
                      borderColor: "rgba(79,70,229,0.5)",
                    },
                  }}
                  onClick={() => handleRowClick(job)}
                >
                  <ListItemText
                    primary={
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, fontSize: 16 }}
                        >
                          {job.job_title}
                        </Typography>
                        {job.company_name && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontSize: 13,
                              ml: { sm: 1 },
                            }}
                          >
                            · {job.company_name}
                          </Typography>
                        )}
                        {job.is_new && (
                          <Chip
                            label="Be the first to apply"
                            color="error"
                            size="small"
                            sx={{ ml: { sm: 1 }, mt: { xs: 0.5, sm: 0 } }}
                          />
                        )}
                      </Stack>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", mb: 0.5, mt: 0.5 }}
                        >
                          {job.location || job.country || "—"}
                          {job.posting_date
                            ? ` • Posting date: ${job.posting_date}`
                            : ""}
                        </Typography>
                        <Box
                          sx={{
                            // color: "text.secondary",
                            color: "#4B5563",
                            mt: 0.3,
                            fontSize: 13.5,
                            lineHeight: 1.6,
                          }}
                          dangerouslySetInnerHTML={{
                            __html: cleanJobDescription(
                              job.job_description
                                ? job.job_description.slice(0, 400) +
                                    (job.job_description.length > 400
                                      ? "..."
                                      : "")
                                : ""
                            ),
                          }}
                        />
                      </>
                    }
                  />
                </ListItemButton>
                <Divider component="li" sx={{ borderColor: "rgba(148,163,184,0.2)" }} />
              </React.Fragment>
            ))}
          </List>

          {pageCount > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
                mb: 1,
              }}
            >
              <Pagination
                count={pageCount}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="medium"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          <Dialog
            open={modalOpen}
            onClose={handleCloseModal}
            maxWidth="md"
            fullWidth
            fullScreen={fullScreen}
            PaperProps={{
              sx: {
                borderRadius: fullScreen ? 0 : 4,
                minHeight: fullScreen ? "100vh" : 480,
                maxWidth: fullScreen ? "100%" : 900,
                width: "100%",
                p: 0,
                background: "linear-gradient(135deg,#f9fafb,#eef2ff)",
                boxShadow: "0 16px 40px rgba(15,23,42,0.35)",
              },
            }}
          >
            <DialogTitle
              sx={{
                position: "relative",
                pb: 0,
                pt: 4,
                textAlign: "center",
                borderBottom: "none",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  position: "absolute",
                  top: 8,
                  right: 16,
                }}
              >
                <IconButton onClick={handleCloseModal} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
              <Typography
                variant={fullScreen ? "h5" : "h4"}
                fontWeight={600}
                sx={{ mt: 1, mb: 0.5 }}
              >
                {selectedJob?.job_title || "Untitled Position"}
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {Array.isArray(selectedJob?.locations) &&
                selectedJob.locations.length > 0
                  ? selectedJob.locations.map((l) => l.name).join(", ")
                  : selectedJob?.location || selectedJob?.country || "—"}
              </Typography>
              {selectedJob?.is_new && (
                <Chip
                  label="Be the first to apply"
                  color="error"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
              <Divider sx={{ my: 3, width: "100%" }} />
            </DialogTitle>

            <DialogContent
              dividers
              sx={{
                px: fullScreen ? 3 : 8,
                py: 4,
                background: "#fafbfc",
              }}
            >
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2.5,
                    justifyContent: fullScreen ? "flex-start" : "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <b>Department:</b> {selectedJob?.department || "—"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <b>Job Types:</b>{" "}
                    {Array.isArray(selectedJob?.job_types) &&
                    selectedJob.job_types.length > 0
                      ? selectedJob.job_types.map((jt) => jt.name).join(", ")
                      : "—"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <b>Openings:</b>{" "}
                    {selectedJob?.openings != null
                      ? selectedJob.openings
                      : "—"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <b>Posting Date:</b>{" "}
                    {selectedJob?.start_date
                      ? new Date(
                          selectedJob.start_date
                        ).toLocaleDateString()
                      : "—"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <b>End Date:</b>{" "}
                    {selectedJob?.end_date
                      ? new Date(
                          selectedJob.end_date
                        ).toLocaleDateString()
                      : "—"}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box
                  sx={{
                    maxHeight: fullScreen ? "50vh" : 420,
                    overflow: "auto",
                    px: 2,
                    py: 2,
                    background: "#fff",
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                >
                  <Box
                    sx={{
                      fontSize: 15.5,
                      color: "#222",
                      lineHeight: 1.7,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: cleanJobDescription(
                        selectedJob?.job_description ||
                          "No description provided."
                      ),
                    }}
                  />
                </Box>
              </Stack>
            </DialogContent>

            <DialogActions
              sx={{
                px: fullScreen ? 3 : 8,
                py: 2,
                justifyContent: "space-between",
              }}
            >
              <Button
                onClick={handleCloseModal}
                variant="text"
                color="inherit"
              >
                Close
              </Button>
              <Button
                onClick={handleApply}
                variant="contained"
                color="primary"
              >
                Apply
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}
